import { useState, useEffect, useRef } from "react";
import { Menu, X, LogOut, Loader2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BrandLogo from "../BrandLogo";
import ThemeToggle from "../ThemeToggle";
import Button from "../Button";
// import AfterLoginHomeHeader from "../ProfileDropdown";
import { useAuth } from "@/app/providers/AuthProvider";
import Modal from "../Modal";
import Setting from "@/features/setting/pages/Setting";

interface NavLinkProps {
    href: string;
    children: React.ReactNode;
    isActive: boolean;
    isExternal?: boolean;
}

const NavLink = ({ href, children, isActive, isExternal = false }: NavLinkProps) => {
    return (
        <div className="relative group h-full flex items-center">
            <Link
                to={href}
                target={isExternal ? "_blank" : undefined}
                className={`text-base font-medium transition-colors ${isActive
                    ? "text-foreground"
                    : "text-muted-foreground dark:text-gray-300 hover:text-foreground"
                    }`}
            >
                {children}
            </Link>
            <div
                className={`absolute bottom-0 left-0 h-0.5 bg-blue-600 transition-all duration-300 ease-out translate-y-4
          ${isActive ? "w-full" : "w-0 group-hover:w-full"}`}
            />
        </div>
    );
};

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const { logout, isLoading } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const mobileMenuRef = useRef<HTMLDivElement>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const [isSettingOpen, setIsSettingOpen] = useState(false);

    const isBuilder = pathname.startsWith('/chatbot-builder');

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await logout();
            setIsMenuOpen(false);
            setIsSettingOpen(false);
            navigate('/login');
        } finally {
            setIsLoggingOut(false);
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isMenuOpen &&
                mobileMenuRef.current &&
                menuButtonRef.current &&
                !mobileMenuRef.current.contains(event.target as Node) &&
                !menuButtonRef.current.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
        };

        if (isMenuOpen) {
            // Use a small timeout to avoid closing immediately after opening
            const timeoutId = setTimeout(() => {
                document.addEventListener("mousedown", handleClickOutside);
            }, 0);

            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [isMenuOpen]);

    const VITE_NEXTJS_URL = import.meta.env.VITE_NEXTJS_URL;




    if (isLoading) return null;

    // Completely hide the global navbar when inside the chatbot builder
    // The ChatbotBuilderLayout provides its own full-screen layout and header
    if (isBuilder) {
        return null;
    }

    return (
        <nav
            className="sticky top-0 z-50 w-full bg-white dark:bg-gray-900 navbar-shadow transition-all duration-300"
        >
            <div className={`${pathname === '/live-chat' ? "w-full" : "container"} px-6 flex h-14 items-center justify-between py-4`}>
                <div className="flex items-center gap-6">
                    {/* Standard Pages: Show Logo */}
                    <Link to={`${VITE_NEXTJS_URL}`} target="_blank" className="flex items-center gap-2">
                        <BrandLogo />
                    </Link>
                </div>
                {
                    !location.pathname.startsWith('/onboarding') &&
                    <div className="flex items-center gap-6">
                        <div className="hidden lg:flex items-center text-[#323232] dark:text-white gap-6">
                            <div className="flex items-center gap-6">
                                <NavLink
                                    href="/login"
                                    isActive={pathname?.startsWith("/login")}
                                >
                                    Log in
                                </NavLink>
                            </div>
                            <span className="w-0.5 h-8 bg-gray-200 dark:bg-gray-700"></span>
                            <ThemeToggle />
                            <div className="hidden lg:flex">
                                <Link to="/register">
                                    <Button className="text-center rounded-full text-base py-1 px-6 h-auto">
                                        Try for Free
                                    </Button>
                                </Link>
                            </div>
                        </div>

                        <div className="lg:hidden flex items-center gap-2">
                            <ThemeToggle />
                            <button className="flex text-[#323232] dark:text-white" onClick={() => setIsMenuOpen(!isMenuOpen)} ref={menuButtonRef}>
                                {isMenuOpen ? (
                                    <X className="h-6 w-6" />
                                ) : (
                                    <Menu className="h-6 w-6" />
                                )}
                            </button>
                        </div>
                    </div>
                }

            </div>

            {/* Mobile menu */}
            {isMenuOpen && !location.pathname.startsWith('/onboarding') && (
                <div className="lg:hidden border-b" ref={mobileMenuRef}>
                    <div className="container py-4 flex flex-col gap-3">
                        <>
                            <Link
                                to="/login"
                                className="px-2 py-2 text-base font-medium text-muted-foreground dark:text-gray-300 hover:text-foreground transition-colors"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Log in
                            </Link>
                            <div className="flex flex-col gap-2">
                                <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                                    <Button className="w-full">
                                        Try for Free
                                    </Button>
                                </Link>
                            </div>
                        </>
                    </div>
                </div>
            )}
            <Modal
                isOpen={isSettingOpen}
                onClose={() => setIsSettingOpen(false)}
                title={
                    <div>
                        <h1 className="text-xl font-bold">Settings</h1>
                        <p className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">
                            Manage your account settings and preferences
                        </p>
                    </div>
                }
                size="4xl"
                showFooter={true}
                actions={[
                    {
                        label: (
                            <div className="flex items-center gap-2">
                                {isLoggingOut ? <Loader2 size={16} className="animate-spin" /> : <LogOut size={16} />}
                                {isLoggingOut ? 'Logging out...' : 'Logout'}
                            </div>
                        ),
                        color: "danger",
                        onClick: handleLogout,
                        disabled: isLoggingOut
                    }
                ]}
            >
                <div className="p-1">
                    <Setting hideHeader={true} />
                </div>
            </Modal>
        </nav>
    );
}


export default Navbar;
